import React, { useEffect, useState } from 'react';
import { injectIntl, defineMessages, IntlShape } from 'react-intl';
import { ActionCreatorWithoutPayload } from '@reduxjs/toolkit';
import { MenuProps, Popover } from 'antd';

import AppConfig from 'src/config/AppConfig';
import {
  StyledDropdown,
  DropdownItem,
  StyledLink,
  StyledUser,
  UserDetails,
  StyledRow,
  StyledText,
  StyledSmallText,
} from './styled';
import { EXTERNAL_LINKS } from 'src/utils/constants';
import {
  displayInTimezone,
  DATE_AND_12H_TIME_FORMAT,
} from 'src/utils/timeUtils';
import dayjs from 'dayjs';
import { AboutPopoverHeader, AboutPopoverContent } from './AboutPopoverContent';
import { CURRENT_CONTINENT } from './constants';
import { COMMON_COLORS, Icon } from '@neteera/neteera-web-nextgen';

type Props = {
  loggedInUserName: string;
  userTenantName: string;
  userTenantTimezone: string;
  logout: ActionCreatorWithoutPayload<'auth/logout'>;
  intl: IntlShape;
  className?: string;
};

const UserMenu = ({
  loggedInUserName,
  userTenantName,
  userTenantTimezone,
  className,
  logout,
  intl,
}: Props): JSX.Element => {
  const [time, setTime] = useState<string>(dayjs().format());

  useEffect(() => {
    setInterval(() => {
      setTime(dayjs().format());
    }, 60 * 1000);
  }, []);

  const items: MenuProps['items'] = [
    {
      key: 'manuals',
      label: (
        <DropdownItem>
          {intl.formatMessage(messages.manualsSubMenuTitle)}
        </DropdownItem>
      ),
      children: manuals.map(manual => ({
        key: manual.order,
        label: manual.fileName ? (
          <StyledLink
            target="_blank"
            rel="noopener noreferrer"
            href={`${AppConfig.MANUALS_BUCKET_LINK}/${manual.fileName}`}
          >
            {intl.formatMessage(manual.displayName)}
          </StyledLink>
        ) : (
          <DropdownItem key={intl.formatMessage(manual.displayName)}>
            {intl.formatMessage(manual.displayName)}
          </DropdownItem>
        ),
        children: manual.children,
      })),
    },
    {
      key: 'contact',
      label: (
        <DropdownItem
          onClick={() => {
            window.location.href = 'mailto:support@neteera.com';
          }}
        >
          {intl.formatMessage(messages.contact)}
        </DropdownItem>
      ),
    },
    {
      key: 'privacyPolicy',
      label: (
        <StyledLink
          target="_blank"
          rel="noopener noreferrer"
          href={EXTERNAL_LINKS.PRIVACY_POLICY}
        >
          {intl.formatMessage(messages.privacyPolicy)}
        </StyledLink>
      ),
    },
    {
      key: 'about',
      label: (
        <Popover
          title={AboutPopoverHeader(CURRENT_CONTINENT)}
          content={AboutPopoverContent(intl, CURRENT_CONTINENT)}
          trigger="hover"
          placement="right"
        >
          <DropdownItem>{intl.formatMessage(messages.about)}</DropdownItem>
        </Popover>
      ),
    },
    {
      key: 'logout',
      label: (
        <DropdownItem onClick={() => logout()} data-cy="logout-btn">
          {intl.formatMessage(messages.logout)}
        </DropdownItem>
      ),
    },
  ];

  return (
    <StyledDropdown menu={{ items }} className={className}>
      <StyledUser data-cy="header-user-menu">
        <UserDetails>
          <StyledRow>
            <StyledText>{`${loggedInUserName},`}</StyledText>
            <StyledText>{userTenantName}</StyledText>
          </StyledRow>
          <StyledRow>
            <StyledSmallText>
              {displayInTimezone(
                time,
                userTenantTimezone,
                DATE_AND_12H_TIME_FORMAT,
              )}
            </StyledSmallText>
          </StyledRow>
        </UserDetails>
        <Icon
          height="1.7rem"
          width="1.7rem"
          color={COMMON_COLORS.greyDark}
          iconName="User"
        />
      </StyledUser>
    </StyledDropdown>
  );
};

const messages = defineMessages({
  logout: {
    id: 'components.UserMenu.logout',
    defaultMessage: 'Logout',
  },
  contact: {
    id: 'components.UserMenu.contact',
    defaultMessage: 'Contact Support',
  },
  privacyPolicy: {
    id: 'components.UserMenu.privacyPolicy',
    defaultMessage: 'Privacy Policy',
  },
  manualsSubMenuTitle: {
    id: 'components.UserMenu.manualsSubMenuTitle',
    defaultMessage: 'Manuals',
  },
  userManual: {
    id: 'components.UserMenu.userManual',
    defaultMessage: 'User Manual',
  },
  ifuManual: {
    id: 'components.UserMenu.ifu',
    defaultMessage: 'IFU Main',
  },
  about: {
    id: 'AppLayout.UserMenu.about',
    defaultMessage: 'About',
  },
});

const manuals = [
  {
    order: '0',
    displayName: messages.ifuManual,
    fileName: 'IFU_Main.pdf',
    children: false,
  },
  {
    order: '1',
    fileName: 'user-manual.pdf',
    displayName: messages.userManual,
    children: false,
  },
];
export default injectIntl(UserMenu);
